import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FIELD } from "@/components/forms/Field";
import { BUSINESS_TYPES, CATEGORIES, INTERESTS } from "@/data/site";
import { submitLead, errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

const EMPTY = { business_type: "", city_state: "", name: "", phone: "", email: "", message: "" };

const Success = ({ name, onReset, idPrefix }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-10" data-testid={`${idPrefix}-form-success`}>
    <div className="grid h-14 w-14 place-items-center rounded-full bg-volt text-ink-950"><Check className="h-6 w-6" /></div>
    <h3 className="mt-6 font-display text-3xl font-bold text-white">Thanks{name ? `, ${name.split(" ")[0]}` : ""}.</h3>
    <p className="mt-3 max-w-md text-slate-400">Your request is in. A Brewer specialist will reach out soon with equipment options for your location.</p>
    <button onClick={onReset} className="btn-ghost mt-8 h-11 px-5 text-sm" data-testid={`${idPrefix}-form-reset`}>Send another request</button>
  </motion.div>
);

export const LeadForm = ({ preset = "", presetInterest = "", source = "lead-form", idPrefix = "lead" }) => {
  const [form, setForm] = useState(EMPTY);
  const [interest, setInterest] = useState(presetInterest);
  const [equipment, setEquipment] = useState(preset ? [preset] : []);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const options = useMemo(() => [...new Set([preset, ...CATEGORIES.map((c) => c.title)].filter(Boolean))], [preset]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (o) => setEquipment((l) => (l.includes(o) ? l.filter((x) => x !== o) : [...l, o]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_type) return toast.error("Please select your business type.");
    setLoading(true);
    try {
      await submitLead({ ...form, equipment, interest, source });
      toast.success("Request received — we'll be in touch soon.");
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success name={form.name} idPrefix={idPrefix} onReset={() => { setForm(EMPTY); setEquipment(preset ? [preset] : []); setInterest(presetInterest); setDone(false); }} />;

  const id = (k) => `${idPrefix}-${k}`;
  return (
    <form onSubmit={submit} className="grid gap-x-8 gap-y-7 sm:grid-cols-2" data-testid={`${idPrefix}-form`}>
      <Field label="I'm interested in" className="sm:col-span-2">
        <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4" role="radiogroup">
          {INTERESTS.map((o) => (
            <button type="button" role="radio" aria-checked={interest === o} key={o} onClick={() => setInterest(o)} className={cn("rounded-xl border px-3 py-3 text-left text-sm transition-[background-color,border-color,color] duration-300", interest === o ? "border-volt bg-volt/10 text-white" : "border-white/10 text-slate-400 hover:border-white/30")} data-testid={`${idPrefix}-interest-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className={cn("mb-2 block h-2 w-2 rounded-full", interest === o ? "bg-volt" : "bg-white/15")} />
              {o}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Business type" required>
        <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
          <SelectTrigger className={cn(FIELD, "justify-between focus:ring-offset-0 [&>span]:text-left")} data-testid={id("business-type-select")}>
            <SelectValue placeholder="Select your business" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-ink-900 text-white" data-lenis-prevent>
            {BUSINESS_TYPES.map((b) => (
              <SelectItem key={b} value={b} data-testid={`${idPrefix}-business-option-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="City and state" htmlFor={id("city")} required>
        <Input id={id("city")} required value={form.city_state} onChange={set("city_state")} placeholder="McMinnville, TN" className={FIELD} data-testid={id("city-input")} />
      </Field>
      <Field label="Equipment interested in" className="sm:col-span-2">
        <div className="mt-3 flex flex-wrap gap-2">
          {options.map((o) => {
            const on = equipment.includes(o);
            return (
              <button type="button" key={o} onClick={() => toggle(o)} aria-pressed={on} className={cn("rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-300", on ? "border-volt bg-volt text-ink-950" : "border-white/15 text-slate-300 hover:border-white/40")} data-testid={`${idPrefix}-equipment-chip-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {o}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Name" htmlFor={id("name")} required>
        <Input id={id("name")} required value={form.name} onChange={set("name")} placeholder="Full name" className={FIELD} data-testid={id("name-input")} />
      </Field>
      <Field label="Phone" htmlFor={id("phone")} required>
        <Input id={id("phone")} type="tel" required minLength={7} value={form.phone} onChange={set("phone")} placeholder="(931) 000-0000" className={FIELD} data-testid={id("phone-input")} />
      </Field>
      <Field label="Email" htmlFor={id("email")} required className="sm:col-span-2">
        <Input id={id("email")} type="email" required value={form.email} onChange={set("email")} placeholder="you@business.com" className={FIELD} data-testid={id("email-input")} />
      </Field>
      <Field label="Message" htmlFor={id("message")} className="sm:col-span-2">
        <Textarea id={id("message")} value={form.message} onChange={set("message")} rows={3} placeholder="Tell us about your space, foot traffic, or anything else." className={cn(FIELD, "min-h-[96px] resize-none py-3")} data-testid={id("message-input")} />
      </Field>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="btn-primary h-14 w-full px-8 text-base disabled:opacity-60 sm:w-auto" data-testid={id("submit-button")}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Get Equipment Options {!loading && <ArrowRight className="btn-arrow h-4 w-4" />}
        </button>
      </div>
    </form>
  );
};
